import React, { useEffect, useRef, useState } from 'react';
import util from '../../styles/utilities.module.css';
import Slider from 'react-slick';
import ReactPlayer from 'react-player';
import styles from './ModuleCarousel.module.css';
import ArrowLeft from '../../images/left-arrow.svg';
import ArrowLeftEmpty from '../../images/left-arrow-empty.svg';
import ArrowRight from '../../images/right-arrow.svg';
import ArrowRightEmpty from '../../images/right-arrow-empty.svg';
import { formatDuration } from '../../utils/credentialingUtils';

const classNames = (...values) => values.filter(Boolean).join(' ');

const STORAGE_KEY_PREFIX = 'credentialing-carousel-position';

const STATUS_VARIANTS = {
  idle: {
    card: styles.cardIdle,
    badge: styles.timeBadgeIdle,
  },
  progress: {
    card: styles.cardProgress,
    badge: styles.timeBadgeProgress,
  },
  done: {
    card: styles.cardDone,
    badge: styles.timeBadgeDone,
  },
};

const getStatusVariant = (status) => STATUS_VARIANTS[status] ?? STATUS_VARIANTS.idle;

function SampleNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{
        ...style,
        display: 'block',
        top: '14%',
      }}
      onClick={onClick}
    >
      <img
        height={20}
        width={20}
        src={className.includes('slick-disabled') ? ArrowRightEmpty : ArrowRight}
        alt="arrow_right"
      />
    </div>
  );
}

function SamplePrevArrow(props) {
  const { className, style, onClick } = props;

  return (
    <div
      className={className}
      style={{
        ...style,
        display: 'block',
        top: '14%',
      }}
      onClick={onClick}
    >
      <img
        height={20}
        width={20}
        src={className.includes('slick-disabled') ? ArrowLeftEmpty : ArrowLeft}
        alt="arrow_left"
      />
    </div>
  );
}

const hasPlayableVideo = (module) => {
  if (!module?.has_video) return false;
  const url = typeof module.video_url === 'string' ? module.video_url.trim() : '';
  if (!url) return false;
  try {
    return ReactPlayer.canPlay(url);
  } catch (error) {
    console.warn('[ModuleCarousel] Unable to evaluate video URL', url, error);
    return false;
  }
};

const getUsageProgress = (usageRequirement) => {
  if (!usageRequirement) {
    return { current: 0, total: 0, done: false };
  }

  const current = Number(usageRequirement.current_count ?? 0);
  const total = Number(usageRequirement.required_quantity ?? 0);
  return {
    current,
    total,
    done: usageRequirement.completed === true || (total > 0 && current >= total),
  };
};

const determineStatus = (module, progress, usageInfo) => {
  const moduleCompleted = module?.is_completed || progress?.is_completed;
  if (moduleCompleted) {
    return 'done';
  }

  if (usageInfo?.done) {
    return 'done';
  }

  const videoProgress = Number(progress?.video_progress_percentage ?? module?.video_progress_percentage ?? 0);
  const quizStatus = progress?.quiz_status ?? module?.quiz_status;
  const quizAttempts = Number(progress?.quiz_attempts ?? module?.quiz_attempts ?? 0);
  const quizPassed = progress?.quiz_passed ?? module?.quiz_passed;
  const usageStarted = usageInfo?.current > 0;
  const moduleProgress = Number(progress?.progress_percentage ?? module?.progress_percentage ?? 0);
  const moduleStarted = Boolean(module?.started || progress?.started_at);
  const videoCompleted = progress?.video_completed ?? module?.video_completed;
  const contentCompleted = progress?.content_completed ?? module?.content_completed;
  const quizCompleted = Boolean(quizPassed || quizStatus === 'passed');

  const hasProgress = Boolean(
    moduleStarted ||
    videoProgress > 0 ||
    quizAttempts > 0 ||
    quizStatus === 'attempted' ||
    quizCompleted ||
    videoCompleted ||
    contentCompleted ||
    moduleProgress > 0 ||
    usageStarted
  );

  return hasProgress ? 'progress' : 'idle';
};

const getVideoCompletionInfo = (module, progress) => {
  const hasVideo = module?.has_video;
  if (!hasVideo) {
    return { available: false, required: false, done: false };
  }

  const required = module?.video_required === true;
  const videoCompletedFlag = progress?.video_completed;
  const fallbackCompleted = module?.video_completed;
  const done = Boolean(videoCompletedFlag ?? fallbackCompleted);

  return { available: true, required, done };
};

const getContentCompletionInfo = (module, progress) => {
  const hasContent = module?.has_text_content;
  if (!hasContent) {
    return { available: false, required: false, done: false };
  }

  const required = module?.content_required === true;
  const contentCompleted = progress?.content_completed;
  const fallbackCompleted = module?.content_completed;
  const done = Boolean(contentCompleted ?? fallbackCompleted);

  return { available: true, required, done };
};

const getQuizCompletionInfo = (module, progress) => {
  const hasQuiz = module?.has_quiz;
  if (!hasQuiz) {
    return { available: false, required: false, done: false, quizStatus: undefined };
  }

  const required = module?.quiz_required === true;
  const quizStatus = progress?.quiz_status ?? module?.quiz_status;
  const quizPassed = progress?.quiz_passed ?? module?.quiz_passed;
  const done = Boolean(quizPassed || quizStatus === 'passed');

  return { available: true, required, done, quizStatus };
};

const buildPillData = (module, progress, usageInfo) => {
  const pillList = [];

  const usageRequired = module?.usage_required ?? module?.requires_usage_requirement;
  if (usageRequired && usageInfo?.total) {
    pillList.push({
      key: 'usage',
      label: `Progress: ${Math.min(usageInfo.current, usageInfo.total)}/${usageInfo.total}`,
      done: usageInfo.done,
    });
  }

  const videoInfo = getVideoCompletionInfo(module, progress);
  if (videoInfo.available && videoInfo.required) {
    pillList.push({ key: 'video', label: 'Video', done: videoInfo.done });
  }

  const contentInfo = getContentCompletionInfo(module, progress);
  if (contentInfo.available && contentInfo.required) {
    pillList.push({ key: 'lesson', label: 'Lesson', done: contentInfo.done });
  }

  const quizInfo = getQuizCompletionInfo(module, progress);
  if (quizInfo.available && quizInfo.required) {
    pillList.push({ key: 'quiz', label: 'Quiz', done: quizInfo.done });
  }

  return { pillList, videoInfo, contentInfo, quizInfo };
};

const getTimeBadgeLabel = (module, usageInfo) => {
  if (module?.is_completed) {
    return '✓ Complete';
  }

  const usageRequired = module?.usage_required ?? module?.requires_usage_requirement;
  if (usageRequired && usageInfo?.total) {
    return `Progress: ${Math.min(usageInfo.current, usageInfo.total)}/${usageInfo.total}`;
  }

  const minutes = Number(module?.estimated_duration ?? 0);
  if (Number.isFinite(minutes) && minutes > 0) {
    return `~${formatDuration(minutes)}`;
  }

  const progressPercentage = Number(module?.progress_percentage ?? 0);
  if (progressPercentage > 0) {
    return `${Math.round(progressPercentage)}%`;
  }

  return '';
};

const shouldShowReflectionButton = (module, progress) => {
  if (!module) return false;

  if (progress?.reflection_submitted_at) {
    return false;
  }

  const reflectionSupported = Boolean(
    module?.requires_reflection ||
    module?.completion_method === 'reflection' ||
    module?.reflection_prompt
  );

  if (!reflectionSupported) {
    return false;
  }

  const quizStatus = progress?.quiz_status ?? module?.quiz_status;
  const quizPassed = quizStatus === 'passed' || progress?.quiz_passed || module?.quiz_passed;
  const hasVideo = hasPlayableVideo(module);
  const videoCompletedFlag = progress?.video_completed ?? module?.video_completed;
  const finishedVideo = hasVideo && Boolean(videoCompletedFlag);

  return Boolean(quizPassed || finishedVideo);
};

const ModuleCarousel = ({ modules, onModuleClick, moduleProgress = {}, badgeId }) => {
  const normalizedModules = Array.isArray(modules) ? modules : [];
  const storageKey = badgeId ? `${STORAGE_KEY_PREFIX}-${badgeId}` : null;
  const sliderRef = useRef(null);
  const skipNextAfterChange = useRef(true);
  const hasModules = normalizedModules.length > 0;
  const slidesToShow = hasModules ? Math.min(normalizedModules.length, 2) : 1;
  const maxSlideIndex = hasModules ? Math.max(0, normalizedModules.length - slidesToShow) : 0;
  const hasHydratedFromStorage = useRef(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!sliderRef.current || typeof sliderRef.current.slickGoTo !== 'function') {
      return;
    }

    skipNextAfterChange.current = true;
    sliderRef.current.slickGoTo(currentSlide, true);
  }, [currentSlide, normalizedModules.length]);

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') {
      return;
    }

    if (!hasModules) {
      return;
    }

    if (!hasHydratedFromStorage.current) {
      const savedValue = window.sessionStorage.getItem(storageKey);
      if (savedValue) {
        const parsedValue = Number.parseInt(savedValue, 10);
        if (!Number.isNaN(parsedValue)) {
          const clampedValue = Math.min(Math.max(parsedValue, 0), maxSlideIndex);
          if (clampedValue !== currentSlide) {
            skipNextAfterChange.current = true;
            setCurrentSlide(clampedValue);
          }
        }
      }
      hasHydratedFromStorage.current = true;
      return;
    }

    if (currentSlide > maxSlideIndex) {
      skipNextAfterChange.current = true;
      persistSlidePosition(maxSlideIndex);
      setCurrentSlide(maxSlideIndex);
    }
  }, [storageKey, hasModules, maxSlideIndex, currentSlide]);

  const persistSlidePosition = (index) => {
    if (!storageKey || typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(storageKey, index.toString());
  };

  const handleAfterChange = (nextIndex) => {
    persistSlidePosition(nextIndex);
    if (skipNextAfterChange.current) {
      skipNextAfterChange.current = false;
      return;
    }

    setCurrentSlide((prev) => {
      if (prev === nextIndex) {
        return prev;
      }
      return nextIndex;
    });
  };

  const showArrows = maxSlideIndex > 0;
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow,
    slidesToScroll: 1,
    arrows: showArrows,
    ...(showArrows ? { nextArrow: <SampleNextArrow />, prevArrow: <SamplePrevArrow /> } : {}),
    dotsClass: styles.buttonBar,
    initialSlide: currentSlide,
    afterChange: handleAfterChange,
  };

  if (!hasModules) {
    return null;
  }

  return (
    <div className={styles.carouselWrapper}>
      <div className={styles.sliderContainer}>
        <Slider ref={sliderRef} {...settings}>
          {normalizedModules.map((module) => {
            const progress = moduleProgress[module.id] || {};
            const usageInfo = getUsageProgress(progress?.usage_requirement ?? module?.usage_requirement);
            const status = determineStatus(module, progress, usageInfo);
            const statusVariant = getStatusVariant(status);
            const { pillList, videoInfo, contentInfo, quizInfo } = buildPillData(module, progress, usageInfo);
            const timeBadgeLabel = getTimeBadgeLabel(module, usageInfo);
            const quizStatus = quizInfo.quizStatus;
            const isVideoPlayable = videoInfo.available && hasPlayableVideo(module);
            const moduleTitle = module?.title?.trim() || 'training module';

            return (
              <div key={module.id} className={styles.slide}>
                <div
                  className={classNames(styles.card, statusVariant.card)}
                  data-testid="credentialing-module-card"
                  data-module-id={module.id}
                >
                  {timeBadgeLabel && (
                    <span className={classNames(styles.timeBadge, statusVariant.badge)}>
                      {timeBadgeLabel}
                    </span>
                  )}

                  <div className={styles.header}>
                    <div className={styles.headerContent}>
                      <h3 className={styles.title}>{module.title}</h3>
                      {module.summary && (
                        <p className={styles.summary}>{module.summary}</p>
                      )}
                    </div>
                  </div>

                  {pillList.length > 0 && (
                    <div className={styles.pills}>
                      {pillList.map((pill) => (
                        <span
                          key={pill.key}
                          className={classNames(styles.pill, pill.done && styles.pillDone)}
                          aria-pressed={pill.done ? 'true' : 'false'}
                          role="img"
                          title={`${pill.label} ${pill.done ? 'completed' : 'not completed'}`}
                        >
                          {pill.done ? '✓' : '○'} {pill.label}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className={styles.actionsRow}>
                    <div className={styles.actionsGroup}>
                      {isVideoPlayable && (
                        <button
                          type="button"
                          data-testid="credentialing-module-action"
                          data-section="video"
                          aria-label={`Open ${moduleTitle} video section`}
                          onClick={() => onModuleClick(module.id, module.slug, 'video')}
                          className={classNames(
                            util.btn,
                            util['btn-sm'],
                            (videoInfo.done || !videoInfo.required) && util['btn--ghost']
                          )}
                        >
                          Watch Video
                        </button>
                      )}

                      {contentInfo.available && (
                        <button
                          type="button"
                          data-testid="credentialing-module-action"
                          data-section="content"
                          aria-label={`View ${moduleTitle} content section`}
                          onClick={() => onModuleClick(module.id, module.slug, 'content')}
                          className={classNames(
                            util.btn,
                            util['btn-sm'],
                            (contentInfo.done || !contentInfo.required) && util['btn--ghost']
                          )}
                        >
                          Learn More
                        </button>
                      )}

                      {quizInfo.available && (
                        <button
                          type="button"
                          data-testid="credentialing-module-action"
                          data-section="quiz"
                          aria-label={`${quizStatus && quizStatus !== 'not_started' ? 'View' : 'Take'} quiz for ${moduleTitle}`}
                          onClick={() => onModuleClick(module.id, module.slug, 'quiz')}
                          className={classNames(
                            util.btn,
                            util['btn-sm'],
                            (quizInfo.done || !quizInfo.required) && util['btn--ghost']
                          )}
                        >
                          {quizStatus && quizStatus !== 'not_started' ? 'View Quiz' : 'Take Quiz'}
                        </button>
                      )}

                      {shouldShowReflectionButton(module, progress) && (
                        <button
                          type="button"
                          data-testid="credentialing-module-action"
                          data-section="reflection"
                          aria-label={`Open reflection for ${moduleTitle}`}
                          onClick={() => onModuleClick(module.id, module.slug, 'reflection')}
                          className={`${util.btn} ${util['btn-sm']}`}
                        >
                          Reflection
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};

export default ModuleCarousel;
